import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Button } from "../../../Components/ui/buttons/button";
import { Trash2, Search } from "../../../Components/ui/icons";
import {
  KeycloakGroupRepresentation,
  KeycloakUserRepresentation,
  UserWithGroupsAndRoles,
} from "../../../global/types/user";
import { formatDate } from "../../../utils/dateUtils";
import UserGroupSelect from "./UserGroupSelect";
import IconButton from "../../../Components/ui/buttons/icon-button";
import Typography from "../../../Components/Typography";

interface UserManagementTableProps {
  users: UserWithGroupsAndRoles[];
  allGroups: KeycloakGroupRepresentation[];
  availableRoles: string[];
  loading?: boolean;
  error?: string | null;
  onDeleteUser: (userId: string) => void;
  onGroupChange: (
    userId: string,
    newGroupId: string | null,
    oldGroupId: string | null
  ) => Promise<void>;
  onRolesChange: (userId: string, newRoles: string) => Promise<void>;
  onRefresh: () => void;
}

export default function UserManagementTable({
  users,
  allGroups,
  availableRoles,
  loading = false,
  error = null,
  onDeleteUser,
  onGroupChange,
  onRolesChange,
  onRefresh,
}: UserManagementTableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] =
    useState<KeycloakUserRepresentation | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleDeleteClick = (user: KeycloakUserRepresentation) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete?.id) {
      onDeleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.firstName?.toLowerCase().includes(lowerSearch) ||
        user.lastName?.toLowerCase().includes(lowerSearch)
    );
  }, [users, searchTerm]);

  // Paginate filtered users
  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar por nome, e-mail ou usuário..."
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <Search size={20} style={{ marginRight: "8px", color: "#666" }} />
          ),
        }}
      />

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="body1">Usuário</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">Perfil de Acesso</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">Criado em</Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">
                    {searchTerm
                      ? "Nenhum usuário encontrado com os critérios de busca."
                      : "Nenhum usuário cadastrado."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body4">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username}
                      </Typography>
                      <Typography variant="body4" className="text-secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.id && (
                      <UserGroupSelect
                        currentValue={
                          user.roles && user.roles.length > 0
                            ? user.roles[0]
                            : undefined
                        }
                        availableOptions={availableRoles}
                        userId={user.id}
                        onChange={onRolesChange}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body4">
                      {formatDate(user.createdTimestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Excluir usuário">
                      <IconButton
                        size="sm"
                        onClick={() => handleDeleteClick(user)}
                        className="color-[#707070] hover:bg-gray-200"
                      >
                        <Trash2 size={20} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredUsers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o usuário{" "}
            <strong>
              {userToDelete?.firstName && userToDelete?.lastName
                ? `${userToDelete.firstName} ${userToDelete.lastName}`
                : userToDelete?.username}
            </strong>
            ? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleDeleteCancel}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleDeleteConfirm}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
